import { ReportChapterObject, ContentObject } from '../../base/types';
import { createScores } from '../../base/scoreHelper';

// Updated chapter definition for Vendors
export const vendors: Omit<ReportChapterObject, 'number'> = {
    id: 'vendors',
    caption: `Vendors`,
    title: `AI vendors deliver specialized tools integrated into the development lifecycle for productivity, security, and modernization`,
    description: `Key AI vendors enhance software development through specialized tools: IDE assistants like Copilot and Gemini boost productivity; security platforms like SonarQube and Snyk automate analysis and fixes; and niche solutions like IBM Watsonx tackle legacy modernization. Common themes include deep workflow integration, large context models (Claude), robust security/privacy options (Tabnine), and combining AI with domain knowledge, aiming to improve code quality and efficiency with human oversight.`,
    editedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
    sections: [
        // OpenAI
        {
            id: 'openai',
            title: `GPT-4 Excels at Complex Coding, Debugging, and Analysis via Code Interpreter`,
            caption: `OpenAI`,
            content: [
                {
                    kind: 'text',
                    text: `OpenAI, founded in 2015 and known for creating ChatGPT and GPT-4, offers large language models for natural dialogue and code generation. Its tools are used broadly by individuals and enterprises via API and ChatGPT Enterprise, achieving massive adoption shortly after launch. Widely seen as a leader in generative AI, its models understand natural language and write code across many languages, supported by a large 32k+ token context.`,
                },
                {
                    kind: 'text',
                    text: `Key features include the Code Interpreter plugin for executing code for analysis and visualization. GPT-4 excels at complex coding tasks, solving ~67% of HumanEval challenges and assisting with debugging and fixing code via iterative execution. While some banks restrict public ChatGPT due to security concerns, many Fortune 500 firms use ChatGPT Enterprise.`,
                },
                {
                    kind: 'text',
                    text: `OpenAI's differentiators include its advanced general AI model with human-like reasoning, a vast knowledge base, code execution ability via Code Interpreter, and continuous improvement fueled by significant R&D resources and funding (~$11B). GPT-4 achieves ~85% accuracy on the HumanEval benchmark.`,
                },
            ],
            keywords: [],
            sources: [],
        },
        // GitHub Copilot
        {
            id: 'github-copilot',
            title: `Copilot Boosts Productivity with In-IDE Suggestions and Test Generation`,
            caption: `GitHub Copilot`,
            content: [
                {
                    kind: 'text',
                    text: `Developed by GitHub and OpenAI since 2021, Copilot acts as an AI pair-programmer directly within developer IDEs like VS Code and JetBrains. It provides in-line code completions and whole-line suggestions, leveraging context from open-source code and the user's own codebase. Aimed at software developers (individuals to enterprise), it boasts over 1 million paying users across 37,000 organizations.`,
                },
                {
                    kind: 'text',
                    text: `Copilot Chat enhances interaction by explaining code and generating tests. It significantly boosts productivity by automating boilerplate code, suggesting routine edits, writing tests, and even catching bugs early by suggesting improvements. Developers report around a 50% reduction in coding time, and internal GitHub data shows over 30% of new code in some projects is generated by Copilot.`,
                },
                {
                    kind: 'text',
                    text: `Its main differentiators include tight IDE integration, training on GitHub's vast open-source code, continuous updates with OpenAI models, and an on-premises option for enterprises (Copilot Business). Forrester recognizes it as a prime example of a "coder TuringBot." Banks like Citi, TD Bank, and UBS are known customers.`,
                },
            ],
            keywords: [],
            sources: [],
        },
        // Anthropic Claude
        {
            id: 'anthropic-claude',
            title: `Claude Offers Large Context and Safe Code Handling for Complex Tasks`,
            caption: `Anthropic Claude`,
            content: [
                {
                    kind: 'text',
                    text: `Anthropic, founded in 2021, offers Claude, a large language model rivaling GPT-4 with a strong focus on helpfulness and safety (v3.5 Sonnet is the latest). It targets enterprises needing private, high-quality LLMs, particularly in regulated industries like finance and health, often accessed via API or Claude Enterprise. Claude is known for fewer hallucinations and better compliance adherence.`,
                },
                {
                    kind: 'text',
                    text: `Key features include an extremely large context window (up to 200K tokens) capable of ingesting entire codebases, strong coding proficiency (solving 64% of internal tasks), and fast, conversational answers. It excels at code understanding and modification, including writing, editing, executing code (with tools), handling bug fixes, and translating legacy code like COBOL.`,
                },
                {
                    kind: 'text',
                    text: `Claude is viewed as a top GPT-4 competitor, backed by significant funding (~$1B+) from partners like Google and Amazon. AWS offers Claude on Bedrock, highlighting its capabilities. Differentiators include its safety focus (Constitutional AI), massive context window, speed, and cost-effectiveness compared to similar models.`,
                },
            ],
            keywords: [],
            sources: [],
        },
        // Google Gemini (Code Assist)
        {
            id: 'google-gemini',
            title: `Gemini Code Assist Provides Integrated Coding Help and Source Transparency`,
            caption: `Google Gemini`,
            content: [
                {
                    kind: 'text',
                    text: `Launched in 2024, Google Gemini Code Assist applies Google's next-gen AI (Gemini) as an AI pair programmer within Google Cloud and IDEs (VS Code/JetBrains). It targets developers on GCP and enterprises seeking cloud-based AI dev tools, offering a free tier for individuals which has boosted early adoption.`,
                },
                {
                    kind: 'text',
                    text: `Features include code completions, full function generation, unit test auto-generation, debugging help, code explanation, and source citations for answers. It assists throughout the software development lifecycle by suggesting fixes during code review, generating documentation, providing debug guidance, and potentially upgrading outdated APIs using Google's code corpus knowledge.`,
                },
                {
                    kind: 'text',
                    text: `Seen as a strong Copilot competitor backed by Google's ecosystem, its differentiators include deep integration with Google tooling (Cloud, DevOps), source-cited answers for transparency, a free individual tier, and the promise of future multimodal (vision+code) features based on the Gemini model.`,
                },
            ],
            keywords: [],
            sources: [],
        },
        // Amazon CodeWhisperer (Q Developer)
        {
            id: 'amazon-q-developer',
            title: `Amazon Q Developer Automates AWS-Centric Tasks with Security Focus`,
            caption: `Amazon Q Developer`,
            content: [
                {
                    kind: 'text',
                    text: `AWS's AI coding assistant, recently rebranded from CodeWhisperer to Amazon Q Developer (GA 2023, rebranded 2024), aims to automate development tasks, especially within AWS environments. It targets AWS developers and enterprise IT, offering a free tier for individuals. While initial adoption lagged Copilot, capabilities are improving with the broader Amazon Q platform.`,
                },
                {
                    kind: 'text',
                    text: `Key features include inline code suggestions optimized for AWS APIs, automated code review, security scan integration, and autonomous agents for debugging, applying upgrades, and documenting code. Q Developer can generate secure code suggestions (filtering credentials, flagging licenses) and perform maintenance like updating deprecated APIs or fixing vulnerabilities.`,
                },
                {
                    kind: 'text',
                    text: `Recognized as Amazon's answer to Copilot, its differentiators are strong AWS integration (understanding SDKs/configs), built-in security scans, IP-safe training data (Amazon code + permissive OSS), and availability on AWS GovCloud/Bedrock for regulated industries. British Telecom (BT) is rolling it out to its developers.`,
                },
            ],
            keywords: [],
            sources: [],
        },
        // Microsoft IntelliCode
        {
            id: 'microsoft-intellicode',
            title: `IntelliCode Automates Repetitive Fixes and Improves Consistency Locally`,
            caption: `Microsoft IntelliCode`,
            content: [
                {
                    kind: 'text',
                    text: `Introduced in 2018, IntelliCode provides AI-assisted coding features within Microsoft Visual Studio and VS Code, serving as a predecessor to Copilot's deep learning approach. Primarily used by enterprise developers (C#, C++, .NET) in Visual Studio, it is widely adopted as it's included by default.`,
                },
                {
                    kind: 'text',
                    text: `Features include whole-line and smart context-aware completions, a local model for privacy (C#), detection of repeated edits with suggestions for project-wide refactoring, and recommendations for common tasks like generating constructors. It automates mundane maintenance by applying pattern fixes across files and generating boilerplate code, improving consistency.`,
                },
                {
                    kind: 'text',
                    text: `Its differentiators are privacy (local model), integration with the familiar IntelliSense UI, and a focus on augmenting human code with high-precision suggestions rather than generating large blocks. Many banks using Visual Studio benefit implicitly.`,
                },
            ],
            keywords: [],
            sources: [],
        },
        // Tabnine
        {
            id: 'tabnine',
            title: `Tabnine Enables Secure, Private AI Coding Assistance On-Premise`,
            caption: `Tabnine`,
            content: [
                {
                    kind: 'text',
                    text: `Tabnine, founded in 2012, is a third-party AI code assistant known for strong privacy options and on-premise solutions, supporting over 20 languages across many IDEs. It serves individual developers (freemium) and enterprises needing on-prem AI, attracting users concerned about Copilot's IP issues.`,
                },
                {
                    kind: 'text',
                    text: `Key features include local inference (offline/VPC deployment for privacy), context-aware completions, and the ability to train on specific projects to adapt to coding styles. It accelerates boilerplate and repetitive coding, particularly useful for maintenance on sensitive codebases behind firewalls. Tabnine Chat can answer code questions based on a user's repository.`,
                },
                {
                    kind: 'text',
                    text: `Often cited as a safer Copilot alternative, Tabnine's differentiators are its IP safety (trained only on permissive code, minimizing license risk), full offline mode, lightweight nature, and flexible model backends. Its compliance-friendly approach has led to adoption in finance, including an investment from Citi.`,
                },
            ],
            keywords: [],
            sources: [],
        },
        // JetBrains AI Assistant
        {
            id: 'jetbrains-ai-assistant',
            title: `JetBrains AI Assistant Leverages IDE Context for Precise Code Insights`,
            caption: `JetBrains AI Assistant`,
            content: [
                {
                    kind: 'text',
                    text: `Launched in preview in 2023, the JetBrains AI Assistant integrates AI features directly into JetBrains IDEs (IntelliJ IDEA, PyCharm, etc.). It targets enterprise and professional developers already using the JetBrains ecosystem.`,
                },
                {
                    kind: 'text',
                    text: `Features include a chat assistant for codebase questions and code change suggestions, Javadoc/KDoc comment generation, in-line error explanation and refactoring suggestions, and integration with IDE navigation. It helps maintain large codebases by summarizing complex classes or suggesting updates, and also generates unit test templates.`,
                },
                {
                    kind: 'text',
                    text: `Its main differentiator is deep integration with JetBrains IDE context (project structure, inspections) for more precise, project-specific help. Future plans include offline work with self-hosted models, addressing data privacy.`,
                },
            ],
            keywords: [],
            sources: [],
        },
        // SonarQube (Sonar AI CodeFix)
        {
            id: 'sonarqube',
            title: `SonarQube Accelerates Remediation with AI-Powered Static Analysis Fixes`,
            caption: `SonarQube`,
            content: [
                {
                    kind: 'text',
                    text: `SonarQube, a leading code quality and security platform since 2008, recently added AI-powered fix suggestions (CodeFix). It serves enterprise dev teams focused on DevSecOps across tech, finance, and government, used by over 400k organizations.`,
                },
                {
                    kind: 'text',
                    text: `SonarQube scans code for bugs, vulnerabilities, and code smells across 30+ languages, integrating with IDEs and CI/CD. The AI CodeFix feature uses GPT-4 to automatically generate fix recommendations for identified issues, allowing developers to resolve problems like code smells and vulnerabilities with one click. This accelerates maintenance, especially for legacy code with technical debt.`,
                },
                {
                    kind: 'text',
                    text: `Its differentiators include unmatched language coverage, deep static analysis rules, and now generative fixes paired with those rules. Sonar ensures AI-proposed fixes still pass its rule engine, providing confidence while keeping a human-in-the-loop for approval.`,
                },
            ],
            keywords: [],
            sources: [],
        },
        // Snyk Code
        {
            id: 'snyk-code',
            title: `Snyk Code Provides Real-Time, AI-Infused Security Feedback in IDE`,
            caption: `Snyk Code`,
            content: [
                {
                    kind: 'text',
                    text: `Launched in 2020, Snyk Code is an AI-infused static application security testing (SAST) tool focused on finding code vulnerabilities and quality issues in real-time. It targets cloud-native and enterprise developers (DevSecOps teams), particularly in tech and financial services.`,
                },
                {
                    kind: 'text',
                    text: `Features include IDE plugins for instant security feedback, ML (from DeepCode acquisition) for low false-positive issue flagging, a comprehensive vulnerability database, and suggestions with data-flow traces. It identifies security and quality issues on the fly, preventing later maintenance headaches. Its AI engine finds tricky bugs and suggests best-practice fixes, reducing remediation burden. An AI auto-fix feature is being piloted.`,
                },
                {
                    kind: 'text',
                    text: `Snyk's differentiators include its developer-first UX (IDE/Git integration), AI-driven reduction in false positives, frequent cloud-enabled rule updates, and an on-prem deployment option. Banks like Nordea and Citibank use Snyk for early vulnerability catching.`,
                },
            ],
            keywords: [],
            sources: [],
        },
        // Checkmarx
        {
            id: 'checkmarx',
            title: `Checkmarx Augments Robust Static Analysis with AI-Guided Remediation`,
            caption: `Checkmarx`,
            content: [
                {
                    kind: 'text',
                    text: `A pioneer in SAST since 2006, Checkmarx automates finding vulnerabilities in source code for large enterprises and government clients across 25+ languages. It integrates into CI/CD and offers configurable rulesets.`,
                },
                {
                    kind: 'text',
                    text: `Checkmarx detects complex security issues (like SQL injection paths) which, if unfixed, become costly maintenance bugs. It is introducing GPT-based query assistants (beta) to help developers interpret results and testing an AI assistant to automatically generate fix pull requests for common vulnerabilities, aiming to reduce manual research effort.`,
                },
                {
                    kind: 'text',
                    text: `Known for robust detection and enterprise support, Checkmarx is a multi-year Gartner MQ Leader. Differentiators include high customizability, wide language coverage, enterprise-grade reporting, and blending accurate static analysis with emerging AI convenience features.`,
                },
            ],
            keywords: [],
            sources: [],
        },
        // Veracode
        {
            id: 'veracode',
            title: `Veracode Offers Comprehensive AST with AI-Driven Prioritization`,
            caption: `Veracode`,
            content: [
                {
                    kind: 'text',
                    text: `Veracode, founded in 2006, provides a cloud-based platform for comprehensive application security testing (SAST, DAST, SCA) primarily for enterprises in finance, retail, and government.`,
                },
                {
                    kind: 'text',
                    text: `Features include static scanning (including unique binary SAST for legacy apps), dynamic web scanning, and AI-driven prioritization of findings based on past data. Veracode flags security flaws and bad practices, preventing maintenance issues. Planned AI-assist features, like natural language vulnerability explanations and fix examples (currently in beta), aim to shorten remediation time, especially for legacy code.`,
                },
                {
                    kind: 'text',
                    text: `A long-standing Gartner MQ leader, Veracode's differentiators include its comprehensive platform, unique binary scanning capability, and a vast dataset of security findings used to train AI for prioritization and fix suggestions. Over 30% of its clients are financial institutions.`,
                },
            ],
            keywords: [],
            sources: [],
        },
        // IBM Watsonx Code Assistant
        {
            id: 'ibm-watsonx',
            title: `IBM Watsonx Automates Legacy COBOL Modernization and Testing`,
            caption: `IBM Watsonx`,
            content: [
                {
                    kind: 'text',
                    text: `Launched in 2023, IBM's Watsonx Code Assistant uses generative AI for code modernization, with a notable focus on COBOL/mainframe systems (Watsonx Code Assistant for Z). It targets large enterprises and governments managing legacy code.`,
                },
                {
                    kind: 'text',
                    text: `Code Assistant for Z analyzes COBOL, provides natural-language summaries, suggests equivalent Java code, ensures semantic equivalence checks, and generates test cases for refactored code. It automates understanding and refactoring of old code, drastically cutting modernization effort and addressing talent/maintenance challenges in banking IT.`,
                },
                {
                    kind: 'text',
                    text: `IBM's differentiators are its unique focus on legacy code (COBOL, PL/I), leveraging deep mainframe expertise, and providing explainability by generating documentation before transformation. Early adopters report high accuracy and significant speed improvements (e.g., 25% faster COBOL-to-Java conversion). Banks like BNP Paribas and Sunlife are exploring or using it.`,
                },
            ],
            keywords: [],
            sources: [],
        },
        // Salesforce CodeT5
        {
            id: 'salesforce-codet5',
            title: `CodeT5 Provides Open-Source Foundation for Specialized Code Tasks`,
            caption: `Salesforce CodeT5`,
            content: [
                {
                    kind: 'text',
                    text: `CodeT5, released by Salesforce AI Research in 2021, is an influential open-source code generation and understanding Transformer model, not a commercial product. It's used by AI researchers and tool builders.`,
                },
                {
                    kind: 'text',
                    text: `Pre-trained on GitHub code, it handles tasks like code completion, summarization, and translation across multiple languages (Python, Java, JS). CodeT5+ (2023) improved accuracy and added security filters. It demonstrates that smaller models (~220M-770M parameters) can handle coding tasks effectively with fine-tuning, potentially enabling on-prem deployment for maintaining proprietary code.`,
                },
                {
                    kind: 'text',
                    text: `Differentiators include its open-source nature (Apache-2.0 license), lightweight size allowing deployment on modest hardware, and fine-tuning capability for specialization (e.g., Salesforce uses it for Apex code).`,
                },
            ],
            keywords: [],
            sources: [],
        },
        // OpenAI Codex (Legacy)
        {
            id: 'openai-codex-legacy',
            title: `Codex Pioneered Generative Code Automation for Routine Tasks (Legacy)`,
            caption: `OpenAI Codex (Legacy)`,
            content: [
                {
                    kind: 'text',
                    text: `Codex (2021–2022) was OpenAI's precursor to ChatGPT for coding, based on GPT-3, and powered the first version of GitHub Copilot. It understood natural language prompts to generate code, excelling at boilerplate tasks but had limitations with complex logic.`,
                },
                {
                    kind: 'text',
                    text: `It demonstrated the feasibility of AI coding, automating tasks like writing SQL queries or UI components from comments, showing potential for handling legacy code maintenance, though it sometimes struggled with complexity. Its API was retired in favor of newer GPT models.`,
                },
                {
                    kind: 'text',
                    text: `Codex had historic impact as the first widely-used generative code model, setting benchmarks. Its differentiators were first-mover advantage and deep GitHub integration, though limited by a smaller context window and less advanced reasoning than successors.`,
                },
            ],
            keywords: [],
            sources: [],
        },
    ],
};
